import fs from "node:fs/promises";
import path from "node:path";
import { COMPLETION_STATUS_TEXT } from "../../core/statuses.js";

/**
 * Codex Adapter - Transforms OpenCode agents, skills, and commands into Codex-native formats.
 */
export class CodexAdapter {
  cwd: string;

  constructor({ cwd }: { cwd: string }) {
    this.cwd = cwd;
  }

  /**
   * Transforms an OpenCode agent into a Codex prompt with completion contract.
   */
  async transformAgent(filePath: string): Promise<{ content: string; name: string }> {
    const content = await fs.readFile(filePath, "utf8");
    const fileName = path.basename(filePath, ".md");
    
    const hardenedContent = `${content}

##  Completion Contract (Mandatory)
Every task completion MUST provide the following payload:
1. **Status**: ${COMPLETION_STATUS_TEXT}
2. **Scope reviewed**: Brief summary of what was analyzed.
3. **Findings**: Evidence-based discoveries.
4. **Files affected**: List of all concrete paths modified or created.
5. **Validation/evidence**: Commands run and their results.
6. **Risks/notes**: Any caveats or technical debt introduced.
7. **Required manager action**: Clear next step for the orchestrator.
8. **Can manager continue**: [yes/no]
`;

    return {
      content: hardenedContent,
      name: fileName
    };
  }

  /**
   * Deploys agents, skills, and commands to Codex-native directories.
   * Mapping:
   * - Agents -> .github/agents/
   * - Skills -> .agents/skills/
   * - Commands -> .codex/prompts/
   */
  async deploy(installRoot: string = ".ai-workflow", force?: boolean): Promise<void> {
    const sourceAgentsDir = path.join(this.cwd, installRoot, "opencode/agents");
    const sourceSkillsDir = path.join(this.cwd, installRoot, "opencode/skills");
    const sourceCommandsDir = path.join(this.cwd, installRoot, "opencode/commands");

    // 1. Deploy Agents to .github/agents/
    const codexAgentsDir = path.join(this.cwd, ".github/agents");
    await fs.mkdir(codexAgentsDir, { recursive: true });
    
    const agentFiles = await fs.readdir(sourceAgentsDir).catch(() => []);
    for (const file of agentFiles) {
      if (!file.endsWith(".md")) continue;
      const transformed = await this.transformAgent(path.join(sourceAgentsDir, file));
      await fs.writeFile(path.join(codexAgentsDir, `${transformed.name}.md`), transformed.content);
    }

    // 2. Deploy Skills to .codex/skills/
    const codexSkillsDir = path.join(this.cwd, ".codex/skills");
    await fs.mkdir(codexSkillsDir, { recursive: true });

    const skillFolders = await fs.readdir(sourceSkillsDir).catch(() => []);
    for (const folder of skillFolders) {
      const skillSource = path.join(sourceSkillsDir, folder, "SKILL.md");
      if (await this.exists(skillSource)) {
        const targetDir = path.join(codexSkillsDir, folder);
        await fs.mkdir(targetDir, { recursive: true });
        const content = await fs.readFile(skillSource, "utf8");
        await fs.writeFile(path.join(targetDir, "SKILL.md"), content);
      }
    }

    // 3. Deploy Commands to .codex/prompts/
    const codexPromptsDir = path.join(this.cwd, ".codex/prompts");
    await fs.mkdir(codexPromptsDir, { recursive: true });

    const commandFiles = await fs.readdir(sourceCommandsDir).catch(() => []);
    for (const file of commandFiles) {
      if (!file.endsWith(".md") && !file.endsWith(".toml")) continue;
      // Codex prompts work best as .md
      const targetFileName = file.endsWith(".toml") ? `${path.basename(file, ".toml")}.md` : file;
      const content = await fs.readFile(path.join(sourceCommandsDir, file), "utf8");
      await fs.writeFile(path.join(codexPromptsDir, targetFileName), content);
    }

    // 4. Copy governance policy into .codex/docs/policies/ so the relative
    //    link ../../docs/policies/SKILLS_COMMON_GOVERNANCE.md in each
    //    .codex/skills/{skill}/SKILL.md resolves correctly.
    const codexPoliciesDir = path.join(this.cwd, ".codex", "docs", "policies");
    await fs.mkdir(codexPoliciesDir, { recursive: true });
    const sourcePoliciesDir = path.join(this.cwd, installRoot, "opencode", "docs", "policies");
    const policyFiles = await fs.readdir(sourcePoliciesDir).catch(() => []);
    for (const file of policyFiles) {
      if (!file.endsWith(".md")) continue;
      const policyContent = await fs.readFile(path.join(sourcePoliciesDir, file), "utf8");
      await fs.writeFile(path.join(codexPoliciesDir, file), policyContent);
    }

    // 5. Create root .github/copilot-instructions.md for maximum Copilot/Codex compatibility
    await this.deployRootCopilotInstructions();

    // 6. Create root CODEX.md for explicit global project contract
    await this.deployRootCodexMd(force);

    // 7. Create root AGENTS.md for compatibility
    await this.deployRootAgentsMd(force);
  }

  async deployRootCopilotInstructions(): Promise<void> {
    const githubDir = path.join(this.cwd, ".github");
    await fs.mkdir(githubDir, { recursive: true });
    const targetPath = path.join(githubDir, "copilot-instructions.md");
    
    const instructions = `# AI Workflow Kit Governance

This repository uses the **AI Workflow Kit** (OpenCode-first) workflow.

##  Mandate: Atlas Authority Protocol
All architectural changes must align with the specifications issued by the **Specification Authority**.

##  Rules & Personas
- Always follow the **Branch Gate** policy (never work on \`main\`).
- Always follow the **SDD Workflow** (Spec -> Plan -> PR -> Evidence).
- When acting as an agent, consult the instructions in \`.github/agents/\`.
- When utilizing skills, consult the files in \`.codex/skills/\`.

##  Completion Contract
Every task completion MUST follow the payload format defined in the individual agent files.
`;
    await fs.writeFile(targetPath, instructions);
  }

  async deployRootCodexMd(force?: boolean): Promise<void> {
    const targetPath = path.join(this.cwd, "CODEX.md");
    if (!force && await this.exists(targetPath)) return;
    
    // Check if the template exists
    const templatePath = path.join(this.cwd, ".ai-workflow", "templates", "CODEX.md.template");
    if (await this.exists(templatePath)) {
      const content = await fs.readFile(templatePath, "utf8");
      await fs.writeFile(targetPath, content);
      return;
    }

    // Fallback to copilot instructions
    const instructions = `# AI Workflow Kit Governance

This repository uses the **AI Workflow Kit** (OpenCode-first) workflow.

##  Mandate: Atlas Authority Protocol
All architectural changes must align with the specifications issued by the **Specification Authority**.

##  Rules & Personas
- Always follow the **Branch Gate** policy (never work on \`main\`).
- Always follow the **SDD Workflow** (Spec -> Plan -> PR -> Evidence).
- When acting as an agent, consult the instructions in \`.github/agents/\`.
- When utilizing skills, consult the files in \`.codex/skills/\`.

##  Completion Contract
Every task completion MUST follow the payload format defined in the individual agent files.
`;
    await fs.writeFile(targetPath, instructions);
  }

  async deployRootAgentsMd(force?: boolean): Promise<void> {
    const targetPath = path.join(this.cwd, "AGENTS.md");
    if (!force && await this.exists(targetPath)) return;
    const templatePath = path.join(this.cwd, ".ai-workflow", "AGENTS.md");
    if (await this.exists(templatePath)) {
      let content = await fs.readFile(templatePath, "utf8");
      // Transform references to point to Codex directories instead of OpenCode/.agents
      content = content.replace(/\.agents\/skills/g, ".codex/skills");
      content = content.replace(/opencode\/commands/g, ".codex/prompts");
      content = content.replace(/opencode\/docs\/policies/g, ".codex/docs/policies");
      await fs.writeFile(targetPath, content);
    }
  }

  async exists(p: string): Promise<boolean> {
    try {
      await fs.access(p);
      return true;
    } catch {
      return false;
    }
  }
}
